import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Accessory } from '#pplx/frontend/aether/components/Accessory/index.js';
import { ButtonPrimitive } from '#pplx/frontend/aether/primitives/ButtonPrimitive/index.js';
import { LinkPrimitive } from '#pplx/frontend/aether/primitives/LinkPrimitive/index.js';
import { cva } from 'class-variance-authority';
import classNames from 'classnames';
const getContentItemClasses = cva('', {
    variants: {
        disabled: {
            false: '',
            true: 'text-secondary',
        },
        highlighted: {
            false: '',
            true: 'bg-soft',
        },
        shouldShowFocusRing: {
            false: 'focus:outline-hidden focus-visible:outline-hidden',
            true: 'interactable',
        },
        isInteractable: {
            false: '',
            true: 'reset hover:bg-subtle cursor-pointer',
        },
        rounded: {
            false: '',
            true: 'rounded-lg',
        },
        size: {
            large: 'gap-3 md:gap-2 md:text-sm',
            default: 'gap-2 text-sm',
        },
        height: {
            dynamic: '',
            md: 'h-16',
        },
        padding: {
            base: 'p-2',
            spacious: 'p-4',
        },
        variant: {
            default: '',
            negative: '',
        },
    },
    compoundVariants: [
        {
            disabled: false,
            variant: 'default',
            class: 'text-primary',
        },
        {
            disabled: false,
            variant: 'negative',
            class: 'text-negative-primary',
        },
    ],
    defaultVariants: {
        disabled: false,
        highlighted: false,
        shouldShowFocusRing: false,
        isInteractable: false,
        rounded: false,
        size: 'default',
        height: 'dynamic',
        padding: 'base',
        variant: 'default',
    },
});
/**
 * Internal implementation of ContentItem with className support.
 */
function ContentItemImpl({ ref, children, leadingAccessory, trailingAccessory, subtitle, textOverflow = 'wrap', disabled = false, highlighted = false, shouldShowFocusRing = false, rounded = false, height = 'dynamic', className, role, href, target, rel, size = 'default', alignItems = 'center', padding = 'base', variant = 'default', ...props }) {
    // Derive interactivity from role: any role makes it interactive
    const isInteractable = Boolean(role);
    // Rendering logic:
    // - If href is provided: render as <a> (with any role, e.g., 'link', 'menuitem')
    // - Else if role="button": render as <button>
    // - Else: render as <div> (with or without role)
    const shouldRenderAsLink = Boolean(href);
    const shouldRenderAsButton = !href && role === 'button';
    const commonClassName = classNames(className, 'w-full flex select-none items-center font-sans', getContentItemClasses({
        disabled,
        highlighted,
        shouldShowFocusRing,
        isInteractable: isInteractable && !disabled,
        rounded,
        size,
        height,
        padding,
        variant,
    }));
    const content = (_jsxs("div", { className: classNames('flex-1 grid grid-cols-[auto_1fr_auto] gap-y-0.5', alignItems === 'start' ? 'items-start' : 'items-center'), children: [leadingAccessory && (_jsx("div", { className: size === 'large' ? 'mr-2.5 md:mr-2' : 'mr-2', children: _jsx(Accessory, { accessory: leadingAccessory, size: size }) })), _jsx("div", { className: classNames('col-start-2 min-w-0', textOverflow === 'ellipsis' && 'truncate'), children: children }), trailingAccessory && (_jsx("div", { className: classNames('col-start-3', size === 'large' ? 'ml-2.5 md:ml-2' : 'ml-2', alignItems === 'start' && 'self-start'), children: _jsx(Accessory, { accessory: trailingAccessory, size: size }) })), subtitle && (_jsx("span", { className: classNames('text-secondary col-start-2 text-xs leading-tight', textOverflow === 'ellipsis' ? 'min-w-0 truncate' : 'whitespace-pre-wrap'), children: subtitle }))] }));
    if (shouldRenderAsButton) {
        return (_jsx(ButtonPrimitive, { ref: ref, type: "button", disabled: disabled, className: commonClassName, ...props, children: content }));
    }
    if (shouldRenderAsLink) {
        if (disabled) {
            return (_jsx("span", { ref: ref, role: role, className: classNames('reset', commonClassName), ...props, children: content }));
        }
        const { onClick, ...linkProps } = props;
        return (_jsx(LinkPrimitive, { ref: ref, role: role, href: href, target: target, rel: rel, 
            // Only apply "interactable" class for role="link" as menuitem roles applies
            // their own focus styles
            __dangerousDoNotUseShouldApplyFocusIndicator: role === 'link', className: commonClassName, __dangerousDoNotUseOnClick: onClick, ...linkProps, children: content }));
    }
    return (_jsx("div", { ref: ref, role: role, className: commonClassName, ...props, children: content }));
}
/**
 * A flexible layout component for displaying content with optional leading/trailing
 * accessories and interactive states. Can be used for menu items, list items,
 * table rows, cards, and other content displays.
 *
 * @example
 * <ContentItem>Primary text</ContentItem>
 *
 * @example
 * <ContentItem
 *   leadingAccessory={IconUser}
 *   trailingAccessory={<Badge>New</Badge>}
 * >
 *   User Profile
 * </ContentItem>
 */
export function ContentItem(props) {
    return _jsx(ContentItemImpl, { ...props });
}
/**
 * Aether-internal version of ContentItem that accepts className.
 */
export function ContentItemInternal(props) {
    return _jsx(ContentItemImpl, { ...props });
}
//# sourceMappingURL=index.js.map